import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { AnimatePresence, motion } from "framer-motion";
import { get_especialidades } from "../../../services/api";
import LogoCardSkeleton from "../../Loading/LogoCardSkeleton";

type CycleOption = { id: string; label: string };

// lo que viene desde la API
type EspecialidadApi = {
  id_especialidad: string;
  name: string;
};


export type EspecialidadOption = { id: string; label: string };


type UserDataWithEspecialidades = {
  especialidades: string[];
  [key: string]: any;
};

interface ConfiguracionObjetivosAprendizajeProps {
  title?: string;
  disabled?: boolean;

  // viene desde UserProfile
  setUserData: React.Dispatch<React.SetStateAction<UserDataWithEspecialidades>>;


  userEspecialidades?: string[];
}

const ConfiguracionObjetivosAprendizaje: React.FC<
  ConfiguracionObjetivosAprendizajeProps
> = ({
  title = "Configuración Objetivos de Aprendizaje",
  disabled = false,
  setUserData,
  userEspecialidades = [],
}) => {
    const cycles: CycleOption[] = useMemo(
      () => [
        { id: "plan_comun", label: "Plan Común" },
        { id: "tp", label: "Técnico profesional" },
      ],
      []
    );

    const [cycleEnabled, setCycleEnabled] = useState<Record<string, boolean>>({
      plan_comun: true,
      tp: true,
    });

    // --- Especialidades ---
    const [especialidades, setEspecialidades] = useState<EspecialidadOption[]>([]);
    const [especialidadesLoading, setEspecialidadesLoading] = useState(false);
    const [especialidadesError, setEspecialidadesError] = useState<string | null>(
      null
    );


    const [objectiveEnabled, setObjectiveEnabled] = useState<
      Record<string, boolean>
    >({});

    // especialidades seleccionadas
    const [selectedEspecialidades, setSelectedEspecialidades] = useState<
      EspecialidadOption[]
    >([]);

    const [objectiveQuery, setObjectiveQuery] = useState("");

    const COLORS = {
      title: "#2E6F8E",
      section: "#567D8C",
      text: "rgba(0,0,0,0.75)",
      switchOn: "#5C7F8A",
      switchOff: "#CFCFCF",
      bg: "#F5F5F5",
      nestedBg: "#F5F5F5",
      nestedBorder: "#F5F5F5",
    };

    const CompactSwitch = (props: React.ComponentProps<typeof Switch>) => (
      <Switch
        size="small"
        disableRipple
        {...props}
        sx={{
          width: 34,
          height: 18,
          padding: 0,
          "& .MuiSwitch-switchBase": {
            padding: 0,
            margin: "2px",
            transitionDuration: "150ms",
            "&.Mui-checked": {
              transform: "translateX(16px)",
              color: "#fff",
              "& + .MuiSwitch-track": {
                backgroundColor: COLORS.switchOn,
                opacity: 1,
              },
            },
            "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.4 },
          },
          "& .MuiSwitch-thumb": {
            width: 14,
            height: 14,
            boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
          },
          "& .MuiSwitch-track": {
            borderRadius: 999,
            backgroundColor: COLORS.switchOff,
            opacity: 1,
          },
          ...props.sx,
        }}
      />
    );

    const tpEnabled = !!cycleEnabled.tp;
    const objectivesDisabled = disabled || !tpEnabled;

    const specialtyItemSx = {
      m: 0,
      width: "100%",
      alignItems: "flex-start",
      gap: 1,
      "& .MuiFormControlLabel-label": {
        fontFamily: "Montserrat",
        fontSize: "0.82rem",
        color: COLORS.text,
        opacity: objectivesDisabled ? 0.6 : 1,
        lineHeight: 1.2,
        display: "block",
        minHeight: 34,
        paddingTop: "2px",
        wordBreak: "break-word",
      },
    };

    // Cargar especialidades desde API
    useEffect(() => {
      let alive = true;

      const load = async () => {
        setEspecialidadesLoading(true);
        setEspecialidadesError(null);

        try {
          const res = await get_especialidades();
          const list: EspecialidadApi[] = res?.data?.data ?? [];

          const mapped: EspecialidadOption[] = list.map((e) => ({
            id: e.id_especialidad,
            label: e.name,
          }));

          if (!alive) return;

          setEspecialidades(mapped);


          setObjectiveEnabled((prev) => {
            const next: Record<string, boolean> = { ...prev };


            mapped.forEach((e) => {
              if (typeof next[e.id] !== "boolean") next[e.id] = false;
            });


            Object.keys(next).forEach((k) => {
              if (!mapped.some((e) => e.id === k)) delete next[k];
            });

            return next;
          });
        } catch (e: any) {
          if (!alive) return;
          setEspecialidadesError(
            e?.message || "No se pudieron cargar las especialidades."
          );
        } finally {
          if (!alive) return;
          setEspecialidadesLoading(false);
        }
      };

      load();

      return () => {
        alive = false;
      };
    }, []);


    useEffect(() => {
      if (!userEspecialidades?.length) return;

      setObjectiveEnabled((prev) => {
        const next = { ...prev };
        userEspecialidades.forEach((id) => {
          next[id] = true;
        });
        return next;
      });
    }, [userEspecialidades]);


    useEffect(() => {
      setSelectedEspecialidades(() => {
        const selected = especialidades.filter((e) => !!objectiveEnabled[e.id]);
        return selected;
      });
    }, [objectiveEnabled, especialidades]);


    useEffect(() => {
      setUserData((prev) => ({
        ...prev,
        especialidades: selectedEspecialidades.map((e) => e.id),
      }));
    }, [selectedEspecialidades, setUserData]);

    // Al apagar TP: apagar especialidades, limpiar búsqueda y limpiar selectedEspecialidades + padre
    useEffect(() => {
      if (!tpEnabled) {
        setObjectiveEnabled((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((k) => (next[k] = false));
          return next;
        });
        setSelectedEspecialidades([]);
        setObjectiveQuery("");

        // limpia también en userData del padre
        setUserData((prev) => ({ ...prev, especialidades: [] }));
      }
    }, [tpEnabled, setUserData]);

    const selectedCount = useMemo(
      () => Object.values(objectiveEnabled).filter(Boolean).length,
      [objectiveEnabled]
    );

    // Listado siempre visible cuando TP está ON
    const filteredObjectives = useMemo(() => {
      const q = objectiveQuery.trim().toLowerCase();
      if (!q) return especialidades;
      return especialidades.filter((o) => o.label.toLowerCase().includes(q));
    }, [objectiveQuery, especialidades]);

    const tpPanelMotion = {
      initial: { opacity: 0, height: 0, y: -6 },
      animate: { opacity: 1, height: "auto", y: 0 },
      exit: { opacity: 0, height: 0, y: -6 },
    } as const;

    const tpPanelRef = useRef<HTMLDivElement | null>(null);
    const prevTpRef = useRef<boolean>(tpEnabled);

    useEffect(() => {
      const prev = prevTpRef.current;

      if (!prev && tpEnabled) {
        requestAnimationFrame(() => {
          setTimeout(() => {
            tpPanelRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }, 120);
        });
      }

      prevTpRef.current = tpEnabled;
    }, [tpEnabled]);

    return (
      <Paper
        elevation={0}
        sx={{
          backgroundColor: COLORS.bg,
          borderRadius: "8px",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          sx={{
            fontFamily: "Montserrat",
            fontSize: { xs: "1.1rem", sm: "24px" },
            fontWeight: 800,
            color: COLORS.title,
            mb: 2,
          }}
        >
          {title}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "0.85rem",
                fontWeight: 800,
                color: COLORS.section,
                letterSpacing: "0.04em",
                mb: 1,
              }}
            >
              CICLO
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              {/* Plan Común */}
              <FormControlLabel
                sx={{
                  m: 0,
                  gap: 1,
                  "& .MuiFormControlLabel-label": {
                    fontFamily: "Montserrat",
                    fontSize: "0.85rem",
                    color: COLORS.text,
                  },
                }}
                control={
                  <CompactSwitch
                    checked={!!cycleEnabled.plan_comun}
                    disabled={disabled}
                    onChange={(e) =>
                      setCycleEnabled((prev) => ({
                        ...prev,
                        plan_comun: e.target.checked,
                      }))
                    }
                  />
                }
                label="Plan Común"
              />

              {/* Técnico profesional */}
              <FormControlLabel
                sx={{
                  m: 0,
                  gap: 1,
                  "& .MuiFormControlLabel-label": {
                    fontFamily: "Montserrat",
                    fontSize: "0.85rem",
                    color: COLORS.text,
                  },
                }}
                control={
                  <CompactSwitch
                    checked={!!cycleEnabled.tp}
                    disabled={disabled}
                    onChange={(e) =>
                      setCycleEnabled((prev) => ({
                        ...prev,
                        tp: e.target.checked,
                      }))
                    }
                  />
                }
                label="Técnico profesional"
              />

              {/* Mensaje cuando TP está OFF */}
              <AnimatePresence initial={false}>
                {!tpEnabled && (
                  <motion.div
                    key="tp-off-hint"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                    style={{ overflow: "hidden" }}
                  >
                    <Typography
                      sx={{
                        mt: 0.75,
                        fontFamily: "Montserrat",
                        fontSize: "0.78rem",
                        color: "rgba(0,0,0,0.55)",
                      }}
                    >
                      Activa “Técnico profesional” para ver y seleccionar
                      especialidades.
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Panel de Especialidades */}
              <AnimatePresence initial={false}>
                {tpEnabled && (
                  <motion.div
                    key="tp-specialties"
                    ref={tpPanelRef}
                    variants={tpPanelMotion}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                    style={{ overflow: "hidden" }}
                  >
                    <Box
                      sx={{
                        mt: 1,
                        ml: { xs: 0, sm: 4 },
                        pl: { xs: 0, sm: 2 },
                        borderLeft: {
                          xs: "none",
                          sm: `3px solid ${COLORS.nestedBorder}`,
                        },
                        backgroundColor: { xs: "transparent", sm: COLORS.nestedBg },
                        borderRadius: "8px",
                        p: { xs: 0, sm: 1.5 },
                        opacity: objectivesDisabled ? 0.7 : 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Montserrat",
                          fontSize: "0.85rem",
                          fontWeight: 800,
                          color: COLORS.section,
                          letterSpacing: "0.04em",
                          mb: 1,
                        }}
                      >
                        Especialidades ({selectedCount} seleccionadas)
                      </Typography>

                      <TextField
                        fullWidth
                        variant="standard"
                        value={objectiveQuery}
                        onChange={(e) => setObjectiveQuery(e.target.value)}
                        placeholder="Buscar especialidad..."
                        disabled={objectivesDisabled || especialidadesLoading}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: "rgba(0,0,0,0.45)" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 1,
                          "& .MuiInputBase-input": {
                            fontFamily: "Montserrat",
                            fontSize: "0.95rem",
                            color: "#1D3D4D",
                          },
                          "& .MuiInput-underline:before": {
                            borderBottomColor: "#E0E0E0",
                          },
                          "& .MuiInput-underline:hover:before": {
                            borderBottomColor: "#567D8C",
                          },
                          "& .MuiInput-underline:after": {
                            borderBottomColor: "#1D3D4D",
                          },
                        }}
                      />

                      {/* Estado de carga / error */}
                      {especialidadesLoading && (
                        <Grid display={"flex"} justifyContent={"center"}>

                          <LogoCardSkeleton />

                        </Grid>
                      )}

                      {!!especialidadesError && (
                        <Typography
                          sx={{
                            mt: 0.5,
                            mb: 1,
                            fontFamily: "Montserrat",
                            fontSize: "0.8rem",
                            color: "rgba(0,0,0,0.6)",
                          }}
                        >
                          {especialidadesError}
                        </Typography>
                      )}

                      <Box sx={{ maxHeight: 300, overflowY: "auto", pr: 1 }}>
                        <Grid container spacing={1} alignItems="stretch">
                          {filteredObjectives.map((o) => (
                            <Grid
                              key={o.id}
                              item
                              xs={12}
                              sm={6}
                              md={4}
                              sx={{ display: "flex" }}
                            >
                              <Box
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  alignItems: "flex-start",
                                  borderRadius: "8px",
                                  px: 1,
                                  py: 0.75,
                                  backgroundColor: "rgba(255,255,255,0.65)",
                                }}
                              >
                                <FormControlLabel
                                  sx={specialtyItemSx}
                                  control={
                                    <CompactSwitch
                                      checked={!!objectiveEnabled[o.id]}
                                      disabled={
                                        objectivesDisabled || especialidadesLoading
                                      }
                                      onChange={(e) => {
                                        const checked = e.target.checked;

                                        setObjectiveEnabled((prev) => ({
                                          ...prev,
                                          [o.id]: checked,
                                        }));

                                        // (opcional) inmediatez visual; el effect de sync también lo mantiene
                                        setSelectedEspecialidades((prev) => {
                                          if (!checked)
                                            return prev.filter((x) => x.id !== o.id);
                                          const exists = prev.some((x) => x.id === o.id);
                                          return exists ? prev : [...prev, o];
                                        });
                                      }}
                                    />
                                  }
                                  label={o.label}
                                />
                              </Box>
                            </Grid>
                          ))}
                        </Grid>

                        {!especialidadesLoading &&
                          filteredObjectives.length === 0 &&
                          especialidades.length > 0 && (
                            <Typography
                              sx={{
                                mt: 1,
                                fontFamily: "Montserrat",
                                fontSize: "0.8rem",
                                color: "rgba(0,0,0,0.55)",
                              }}
                            >
                              No se encontraron especialidades con ese término.
                            </Typography>
                          )}

                        {!especialidadesLoading &&
                          especialidades.length === 0 &&
                          !especialidadesError && (
                            <Typography
                              sx={{
                                mt: 1,
                                fontFamily: "Montserrat",
                                fontSize: "0.8rem",
                                color: "rgba(0,0,0,0.55)",
                              }}
                            >
                              No hay especialidades disponibles.
                            </Typography>
                          )}
                      </Box>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    );
  };

export default ConfiguracionObjetivosAprendizaje;
